import moment from 'moment-timezone';
import {TrainingCapacityStatus} from './types';

const SG_TZ = 'Asia/Singapore';

const CAPACITY_STATUS_LABELS: Record<TrainingCapacityStatus, string> = {
  no_applicants: 'No applicants',
  within_capacity: 'Within capacity',
  exceeds_capacity: 'Exceeds capacity',
  past_completed: 'Past/Completed',
};

const CALENDAR_TILE_COLORS: Record<TrainingCapacityStatus, string> = {
  no_applicants: '#FFFFFF',
  within_capacity: '#DEF0FD',
  exceeds_capacity: '#FFE4E6',
  past_completed: '#FFFFFF',
};

const CALENDAR_TILE_TEXT_COLORS: Record<TrainingCapacityStatus, string> = {
  no_applicants: '#22234e',
  within_capacity: '#155fa6',
  exceeds_capacity: '#b21f3a',
  past_completed: '#22234e',
};

const CAPACITY_PILL_CLASS: Record<TrainingCapacityStatus, string> = {
  no_applicants: 'tm-pill-neutral',
  within_capacity: 'tm-pill-blue',
  exceeds_capacity: 'tm-pill-red',
  past_completed: 'tm-pill-past',
};

const formatDateLabel = (dateValue: string) => moment.tz(dateValue, SG_TZ).format('DD MMM YYYY');
const formatDateInput = (dateValue: moment.Moment) => dateValue.clone().tz(SG_TZ).format('YYYY-MM-DD');
const toLegacyDateValue = (value: string): Date | null => {
  const parsed = moment.tz(value, 'YYYY-MM-DD', true, SG_TZ);
  return parsed.isValid() ? parsed.toDate() : null;
};
const fromLegacyDateValue = (value: Date | null): string =>
  value && !Number.isNaN(value.getTime()) ? formatDateInput(moment(value)) : '';
const formatTimeRange = (startTime: string, endTime: string) => {
  const start = moment.tz(startTime, 'HH:mm', SG_TZ).format('h:mma');
  const end = moment.tz(endTime, 'HH:mm', SG_TZ).format('h:mma');
  return `${start}\u2013${end}`;
};

const formatEventHeaderDate = (dateValue: string) => moment.tz(dateValue, SG_TZ).format('ddd, D MMM YYYY');

const formatEventHeaderTime = (startTime: string, endTime: string) => {
  const start = moment.tz(startTime, 'HH:mm', SG_TZ).format('h:mm A');
  const end = moment.tz(endTime, 'HH:mm', SG_TZ).format('h:mm A');
  return `${start} \u2013 ${end}`;
};

const startOfCurrentWeek = () => moment().tz(SG_TZ).startOf('isoWeek');

const getCalendarVisibleRange = (anchorDate: moment.Moment) => {
  const start = anchorDate.clone().tz(SG_TZ).startOf('isoWeek');
  const end = start.clone().add(27, 'days').endOf('day');
  return {start, end};
};

const buildCalendarRangeLabel = (anchorDate: moment.Moment) => {
  const {start, end} = getCalendarVisibleRange(anchorDate);
  return `${start.format('DD MMM')} \u2013 ${end.format('DD MMM YYYY')}`;
};

const addCalendarWindow = (anchorDate: moment.Moment, direction: 'prev' | 'next') =>
  anchorDate.clone().add(direction === 'next' ? 4 : -4, 'weeks').startOf('isoWeek');

const buildRangeQuery = (from: string, to: string, trainingTypes: string[], capacityStatuses: string[]) => {
  const query = new URLSearchParams();
  query.set('from', from);
  query.set('to', to);
  trainingTypes.forEach(type => query.append('training_types', type));
  capacityStatuses.forEach(status => query.append('capacity_statuses', status));
  return query;
};

const getCreatePreviewDates = (
  isRecurring: boolean,
  date: string,
  startDate: string,
  endDate: string,
  daysOfWeek: number[],
  excludePublicHolidays: boolean,
  publicHolidays: string[],
) => {
  if (!isRecurring) {
    return date ? [date] : [];
  }

  if (!startDate || !endDate || !daysOfWeek.length) {
    return [];
  }

  const selectedDays = new Set(daysOfWeek);
  const holidaySet = new Set(publicHolidays);
  const dates: string[] = [];
  let cursor = moment.tz(startDate, 'YYYY-MM-DD', SG_TZ).startOf('day');
  const end = moment.tz(endDate, 'YYYY-MM-DD', SG_TZ).startOf('day');

  while (cursor.isSameOrBefore(end, 'day')) {
    const current = cursor.format('YYYY-MM-DD');
    if (selectedDays.has(cursor.isoWeekday())) {
      const isHoliday = holidaySet.has(current);
      if (!excludePublicHolidays || !isHoliday) {
        dates.push(current);
      }
    }
    cursor = cursor.add(1, 'day');
  }

  return dates;
};

const toFeeLabel = (fee: number) => `$${Number(fee || 0).toFixed(2)}`;

export {
  SG_TZ,
  CAPACITY_STATUS_LABELS,
  CALENDAR_TILE_COLORS,
  CALENDAR_TILE_TEXT_COLORS,
  CAPACITY_PILL_CLASS,
  formatDateLabel,
  formatDateInput,
  toLegacyDateValue,
  fromLegacyDateValue,
  formatTimeRange,
  formatEventHeaderDate,
  formatEventHeaderTime,
  startOfCurrentWeek,
  getCalendarVisibleRange,
  buildCalendarRangeLabel,
  addCalendarWindow,
  buildRangeQuery,
  getCreatePreviewDates,
  toFeeLabel,
};
